import { z } from 'zod/v3';

export const PersonGroupRuleFieldSchema = z.enum([
  'department',
  'type',
  'title',
  'status',
  'roleId',
]);

export type PersonGroupRuleField = z.infer<typeof PersonGroupRuleFieldSchema>;

export const PersonGroupRuleOperatorSchema = z.enum(['eq', 'in', 'contains']);

export type PersonGroupRuleOperator = z.infer<typeof PersonGroupRuleOperatorSchema>;

export const PersonGroupRuleClauseSchema = z.object({
  field: PersonGroupRuleFieldSchema,
  op: PersonGroupRuleOperatorSchema,
  value: z.union([z.string(), z.array(z.string())]),
});

export type PersonGroupRuleClause = z.infer<typeof PersonGroupRuleClauseSchema>;

export const PersonGroupRuleSchema = z.object({
  all: z.array(PersonGroupRuleClauseSchema).optional(),
  any: z.array(PersonGroupRuleClauseSchema).optional(),
});

export type PersonGroupRule = z.infer<typeof PersonGroupRuleSchema>;

export const PersonGroupMemberSummaryDTOSchema = z.object({
  employeeId: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});

export type PersonGroupMemberSummaryDTO = z.infer<
  typeof PersonGroupMemberSummaryDTOSchema
>;

export const PersonGroupSystemKeySchema = z.enum([
  'active_employees',
  'offboarding_employees',
  'engineering_employees',
  'managers',
  'contractors',
  'healthcare_data_handlers',
]);

export type PersonGroupSystemKey = z.infer<typeof PersonGroupSystemKeySchema>;

export const PersonGroupDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  systemKey: PersonGroupSystemKeySchema.nullable(),
  rule: PersonGroupRuleSchema.nullable(),
  memberCount: z.number().optional(),
  members: z.array(PersonGroupMemberSummaryDTOSchema).optional(),
  linkedUserCount: z.number().optional(),
  assignedPolicyCount: z.number().optional(),
  assignedTrainingProgramCount: z.number().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type PersonGroupDTO = z.infer<typeof PersonGroupDTOSchema>;

export const CreatePersonGroupDTOSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).nullable().optional(),
  rule: PersonGroupRuleSchema.nullable().optional(),
});

export type CreatePersonGroupDTO = z.infer<typeof CreatePersonGroupDTOSchema>;

export const UpdatePersonGroupDTOSchema = z.object({
  id: z.string(),
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  rule: PersonGroupRuleSchema.nullable().optional(),
});

export type UpdatePersonGroupDTO = z.infer<typeof UpdatePersonGroupDTOSchema>;

export const PersonGroupMemberSourceSchema = z.enum([
  'rule',
  'manual_include',
  'rule_and_manual_include',
]);

export type PersonGroupMemberSource = z.infer<typeof PersonGroupMemberSourceSchema>;

export const PersonGroupEffectiveMemberDTOSchema = z.object({
  employeeId: z.string(),
  userId: z.string().nullable(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  title: z.string().nullable(),
  department: z.string().nullable(),
  type: z.string(),
  status: z.string(),
  source: PersonGroupMemberSourceSchema,
});

export type PersonGroupEffectiveMemberDTO = z.infer<
  typeof PersonGroupEffectiveMemberDTOSchema
>;

export const PersonGroupMemberOverrideDTOSchema = z.object({
  employeeId: z.string(),
  excluded: z.boolean(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  title: z.string().nullable(),
  department: z.string().nullable(),
  type: z.string(),
  status: z.string(),
});

export type PersonGroupMemberOverrideDTO = z.infer<
  typeof PersonGroupMemberOverrideDTOSchema
>;

export const SetPersonGroupMemberOverrideDTOSchema = z.object({
  groupId: z.string(),
  employeeId: z.string(),
  mode: z.enum(['include', 'exclude', 'clear']),
});

export type SetPersonGroupMemberOverrideDTO = z.infer<
  typeof SetPersonGroupMemberOverrideDTOSchema
>;

export const GroupAssignmentDTOSchema = z.object({
  id: z.string(),
  groupId: z.string(),
  policyDocumentId: z.string().nullable(),
  trainingProgramId: z.string().nullable(),
  dueDateDays: z.number().nullable(),
  createdAt: z.coerce.date(),
});

export type GroupAssignmentDTO = z.infer<typeof GroupAssignmentDTOSchema>;

export const ReplacePolicyGroupAssignmentsDTOSchema = z.object({
  policyDocumentId: z.string(),
  groupIds: z.array(z.string()).max(50),
  dueDateDays: z.number().int().positive().nullable().optional(),
});

export type ReplacePolicyGroupAssignmentsDTO = z.infer<
  typeof ReplacePolicyGroupAssignmentsDTOSchema
>;

export const ReplaceTrainingGroupAssignmentsDTOSchema = z.object({
  trainingProgramId: z.string(),
  groupIds: z.array(z.string()).max(50),
  dueDateDays: z.number().int().positive().nullable().optional(),
});

export type ReplaceTrainingGroupAssignmentsDTO = z.infer<
  typeof ReplaceTrainingGroupAssignmentsDTOSchema
>;
